import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, X, AlertCircle, Eye } from 'lucide-react';
import Layout from './Layout';
import { groupUiViewKeys } from '../utils/uiViewSections';
import {
    listUiViewProfilesApi,
    createUiViewProfileApi,
    updateUiViewProfileApi,
    deleteUiViewProfileApi,
    listUiViewKeysApi,
} from '../services/api';

/**
 * Admin page for managing UI View Profiles.
 *
 * <p>Layout:
 *  - Left column: list of existing profiles with counts and a "New" button.
 *  - Right column: edit form for the selected / new profile — name, description,
 *    and a grouped checkbox grid of every UiViewKey.
 *
 * <p>The key reference list is fetched from {@code GET /ui-view-profiles/keys}
 * so the frontend doesn't need to keep a copy in sync with the backend enum.
 *
 * <p>Wrapped in {@link Layout} the same way {@code JobsPage}, {@code TeamPage},
 * etc. are, so the left nav sidebar and the user profile dropdown remain
 * visible on this page.
 */
const UiViewProfilesPage = () => {
    const [profiles, setProfiles] = useState([]);
    const [availableKeys, setAvailableKeys] = useState([]);
    const [selected, setSelected] = useState(null); // profile being edited, or null for "new"
    const [isCreating, setIsCreating] = useState(false);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [allowedViews, setAllowedViews] = useState(new Set());

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // ── Initial load ──────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [profilesData, keysData] = await Promise.all([
                    listUiViewProfilesApi(),
                    listUiViewKeysApi(),
                ]);
                if (cancelled) return;
                setProfiles(profilesData || []);
                setAvailableKeys(keysData || []);
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.message || 'Failed to load UI view profiles');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Form population when selection changes ────────────────────────

    useEffect(() => {
        if (isCreating) {
            setName('');
            setDescription('');
            setAllowedViews(new Set());
        } else if (selected) {
            setName(selected.name || '');
            setDescription(selected.description || '');
            setAllowedViews(new Set(selected.allowedViews || []));
        } else {
            setName('');
            setDescription('');
            setAllowedViews(new Set());
        }
        setError(null);
    }, [selected, isCreating]);

    // ── Grouping for display ──────────────────────────────────────────

    /**
     * Group the key reference list by the first word of the label for
     * readable sectioning in the checkbox grid. Backend labels already
     * map well — "Bookings", "Resource Planning", etc.
     */
    const keySections = useMemo(() => groupUiViewKeys(availableKeys), [availableKeys]);

    // ── Event handlers ────────────────────────────────────────────────

    const handleToggle = (key) => {
        setAllowedViews(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSelectAllInSection = (keys, selectAll) => {
        setAllowedViews(prev => {
            const next = new Set(prev);
            keys.forEach(k => {
                if (selectAll) next.add(k.key);
                else next.delete(k.key);
            });
            return next;
        });
    };

    const handleNew = () => {
        setSelected(null);
        setIsCreating(true);
    };

    const handleCancel = () => {
        setSelected(null);
        setIsCreating(false);
    };

    const handleSave = async () => {
        setError(null);
        if (!name.trim()) {
            setError('Name is required');
            return;
        }

        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            allowedViews: Array.from(allowedViews),
        };

        setSaving(true);
        try {
            if (isCreating) {
                const created = await createUiViewProfileApi(payload);
                setProfiles(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
                setSelected(created);
                setIsCreating(false);
            } else {
                const updated = await updateUiViewProfileApi(selected.uuid, payload);
                setProfiles(prev =>
                    prev.map(p => (p.uuid === updated.uuid ? updated : p))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
                setSelected(updated);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        if (!window.confirm(`Delete UI view profile "${selected.name}"?`)) return;

        setSaving(true);
        setError(null);
        try {
            await deleteUiViewProfileApi(selected.uuid);
            setProfiles(prev => prev.filter(p => p.uuid !== selected.uuid));
            setSelected(null);
            setIsCreating(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete profile');
        } finally {
            setSaving(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Layout>
                <div className="p-6">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </Layout>
        );
    }

    const editing = isCreating || selected !== null;

    return (
        <Layout>
            <div className="p-4 sm:p-6">
                {/* Header */}
                <div className="mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <Eye size={20} className="text-emerald-700" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">UI View Profiles</h1>
                        <p className="text-sm text-gray-500">
                            Control which nav items each staff member sees. Owners always see everything.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* ── Left: list ───────────────────────────────── */}
                    <div className="lg:col-span-1">
                        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                                <h2 className="text-sm font-semibold text-gray-900">Profiles</h2>
                                <button
                                    onClick={handleNew}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                                >
                                    <Plus size={14} />
                                    New
                                </button>
                            </div>

                            {profiles.length === 0 ? (
                                <div className="p-6 text-center text-sm text-gray-500">
                                    No profiles yet. Click "New" to create one.
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-100">
                                    {profiles.map(p => {
                                        const isSelected = selected?.uuid === p.uuid && !isCreating;
                                        return (
                                            <li key={p.uuid}>
                                                <button
                                                    onClick={() => { setIsCreating(false); setSelected(p); }}
                                                    className={`w-full text-left px-4 py-3 transition-colors ${
                                                        isSelected
                                                            ? 'bg-emerald-50 border-l-2 border-emerald-600'
                                                            : 'hover:bg-gray-50 border-l-2 border-transparent'
                                                    }`}
                                                >
                                                    <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {(p.allowedViews || []).length} view{(p.allowedViews || []).length === 1 ? '' : 's'}
                                                    </p>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* ── Right: editor ──────────────────────────────── */}
                    <div className="lg:col-span-2">
                        {!editing ? (
                            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-gray-500 text-sm">
                                Select a profile to edit, or click "New" to create one.
                            </div>
                        ) : (
                            <div className="bg-white border border-gray-200 rounded-2xl">
                                {/* Editor header */}
                                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                                    <h2 className="text-sm font-semibold text-gray-900">
                                        {isCreating ? 'New UI View Profile' : `Edit: ${selected?.name}`}
                                    </h2>
                                    <div className="flex items-center gap-2">
                                        {!isCreating && selected && (
                                            <button
                                                onClick={handleDelete}
                                                disabled={saving}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        )}
                                        <button
                                            onClick={handleCancel}
                                            disabled={saving}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            <X size={14} />
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                                        >
                                            <Save size={14} />
                                            {saving ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </div>

                                {/* Error */}
                                {error && (
                                    <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                        <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                )}

                                {/* Form body */}
                                <div className="p-4 space-y-5">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="e.g. Service Advisor"
                                            maxLength={100}
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Description <span className="text-gray-400 font-normal">(optional)</span>
                                        </label>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            rows={2}
                                            placeholder="Short description of what this profile is for"
                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none resize-none"
                                        />
                                    </div>

                                    {/* Allow-list */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-xs font-semibold text-gray-700">
                                                Allowed UI Views <span className="text-gray-400 font-normal">({allowedViews.size} selected)</span>
                                            </label>
                                        </div>

                                        <div className="space-y-4">
                                            {keySections.map(section => {
                                                const allSelected = section.keys.every(k => allowedViews.has(k.key));
                                                return (
                                                    <div key={section.label} className="border border-gray-200 rounded-lg">
                                                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                                {section.label}
                                                            </p>
                                                            <button
                                                                onClick={() => handleSelectAllInSection(section.keys, !allSelected)}
                                                                className="text-xs font-medium text-emerald-700 hover:text-emerald-800"
                                                            >
                                                                {allSelected ? 'Deselect all' : 'Select all'}
                                                            </button>
                                                        </div>
                                                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                            {section.keys.map(k => (
                                                                <label
                                                                    key={k.key}
                                                                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={allowedViews.has(k.key)}
                                                                        onChange={() => handleToggle(k.key)}
                                                                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                                    />
                                                                    <span className="text-sm text-gray-700">{k.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default UiViewProfilesPage;